import MyPeople from "@/pages/MyPeople";
import Journal from "@/pages/Journal";
import ConnectionView from "@/pages/ConnectionView";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ClientDashboard from "@/pages/ClientDashboard";
import ClinicianDashboard from "@/pages/ClinicianDashboard";
import Community from "@/pages/Community";
import Learn from "@/pages/Learn";
import Billing from "@/pages/Billing";
import PaymentResult from "@/pages/PaymentResult";
import { BillingBanner } from "@/components/BillingBanner";
import { IdleLogout } from "@/components/IdleLogout";
import { MfaGraceBanner } from "@/components/MfaGraceBanner";
import { InstallPrompt } from "@/components/InstallPrompt";
import { DemoBanner } from "@/components/DemoBanner";
import Security from "@/pages/Security";
import AiConsent from "@/pages/AiConsent";
import { FeedbackButton } from "@/components/FeedbackButton";
import Privacy from "@/pages/Privacy";
import StylePreview from "@/pages/StylePreview";
import Beta from "@/pages/Beta";
import BetaBoard from "@/pages/BetaBoard";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";

/** New clients decide about AI before the dashboard (and any AI) loads. */
function ConsentGate({ children }) {
  const { user } = useAuth();
  if (user && user.role === "client" && user.ai_prefs_confirmed === false) return <Navigate to="/welcome/ai" replace />;
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user && user.role) return <Navigate to={user.role === "clinician" ? "/clinician" : "/client"} replace />;
  return <Landing />;
}

function AppShell() {
  return (
    <div className="App min-h-screen bg-paper text-stone-900 font-sans antialiased grain">
      <Header />
      <DemoBanner />
      <BillingBanner />
      <MfaGraceBanner />
      <IdleLogout />
      <InstallPrompt />
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/style-preview" element={<StylePreview />} />
        <Route path="/beta" element={<Beta />} />
        <Route path="/admin/beta" element={<ProtectedRoute><BetaBoard /></ProtectedRoute>} />
        <Route path="/security" element={<ProtectedRoute><Security /></ProtectedRoute>} />
        <Route path="/people" element={<ProtectedRoute><MyPeople /></ProtectedRoute>} />
        <Route path="/people/:cid" element={<ProtectedRoute><ConnectionView /></ProtectedRoute>} />
        <Route path="/journal" element={<ProtectedRoute role="client"><Journal /></ProtectedRoute>} />
        <Route path="/welcome/ai" element={<ProtectedRoute role="client"><AiConsent /></ProtectedRoute>} />
        <Route
          path="/client"
          element={
            <ProtectedRoute role="client">
              <ConsentGate><ClientDashboard /></ConsentGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/clinician"
          element={
            <ProtectedRoute role="clinician">
              <ClinicianDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/community"
          element={
            <ProtectedRoute role="client">
              <Community />
            </ProtectedRoute>
          }
        />
        <Route
          path="/learn"
          element={
            <ProtectedRoute>
              <Learn />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute>
              <Billing />
            </ProtectedRoute>
          }
        />
        <Route path="/payment/success" element={<ProtectedRoute><PaymentResult /></ProtectedRoute>} />
        <Route path="/payment/cancel" element={<ProtectedRoute><PaymentResult cancelled /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <FeedbackButton />
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#FFFFFF",
            border: "1px solid #E8E2D8",
            color: "#1C1917",
            borderRadius: "1rem",
          },
        }}
      />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}
